import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { clearSession } from "@/lib/api";
import {
  LayoutDashboard, Users, ShoppingCart, FileText, Truck, Undo2,
  ScanBarcode, Wallet, Receipt, Landmark, Banknote, FileClock,
  BarChart3, Settings, Bell, LogOut, ClipboardList, PackageSearch,
  ShoppingBag, HandCoins, MessageSquare, Boxes, Phone, Package, BookOpen, Building2, Crown,
  Warehouse, Truck as TruckIcon, BellRing, Database, ClipboardCheck, Menu, X as XIcon
} from "lucide-react";
import { useEffect, useState } from "react";
import api from "@/lib/api";

function PlanBadge() {
  const [plan, setPlan] = useState(null);
  const [trialEnd, setTrialEnd] = useState(null);
  useEffect(() => {
    api.get("/firms/me").then(({ data }) => {
      setPlan(data.plan || "professional");
      if (data.trial && data.trial_end_at) {
        try { setTrialEnd(new Date(data.trial_end_at)); } catch {}
      }
    }).catch(() => {});
  }, []);
  if (!plan) return null;
  const daysLeft = trialEnd ? Math.max(0, Math.ceil((trialEnd - new Date()) / (1000 * 60 * 60 * 24))) : null;
  const color = plan === "business" ? "bg-emerald-50 text-emerald-800 border-emerald-200" :
                plan === "professional" ? "bg-blue-50 text-blue-800 border-blue-200" :
                "bg-slate-50 text-slate-700 border-slate-200";
  return (
    <a href="/pricing" className={`flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-1 rounded-md border ${color}`} title="View plans" data-testid="plan-badge">
      <Crown size={11} />
      <span className="font-bold">{plan}</span>
      {daysLeft !== null && <span className="ml-1">· {daysLeft}d trial</span>}
    </a>
  );
}

const accountantNav = [
  { to: "/app/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/app/parties", label: "Parties", icon: Users },
  { to: "/app/items", label: "Items / Stock", icon: Boxes },
  { to: "/app/warehouses", label: "Warehouses & Bins", icon: Warehouse },
  { to: "/app/stock-transit", label: "Stock in Transit", icon: TruckIcon },
  { to: "/app/sales", label: "Sales", icon: ShoppingCart },
  { to: "/app/estimates", label: "Quotations / Estimates", icon: FileText },
  { to: "/app/proforma", label: "Proforma", icon: FileText },
  { to: "/app/pay-in", label: "Payment In", icon: HandCoins },
  { to: "/app/pending-orders", label: "Pending Orders", icon: ClipboardList },
  { to: "/app/delivery-challan", label: "Delivery Challan", icon: Truck },
  { to: "/app/credit-notes", label: "Credit Note / Return", icon: Undo2 },
  { to: "/app/pos", label: "POS", icon: ScanBarcode },
  { to: "/app/purchases", label: "Purchase Bills", icon: ShoppingBag },
  { to: "/app/purchase-orders", label: "Purchase Orders", icon: ClipboardCheck },
  { to: "/app/item-requests", label: "Item Requests", icon: BellRing },
  { to: "/app/pay-out", label: "Payment Out", icon: Wallet },
  { to: "/app/expenses", label: "Expenses", icon: Receipt },
  { to: "/app/pending-purchase", label: "Pending Purchase", icon: PackageSearch },
  { to: "/app/debit-notes", label: "Purchase Return / Debit", icon: Undo2 },
  { to: "/app/bank", label: "Bank", icon: Landmark },
  { to: "/app/cash", label: "Cash in Hand", icon: Banknote },
  { to: "/app/cheques", label: "Cheques", icon: FileClock },
  { to: "/app/loans", label: "Loan Accounts", icon: HandCoins },
  { to: "/app/reports", label: "Reports", icon: BarChart3 },
  { to: "/app/bulk-export", label: "Bulk PDF Export", icon: Package },
  { to: "/app/data-import", label: "Import & Tally", icon: Database },
  { to: "/app/manual", label: "User Manual", icon: BookOpen },
];

const ownerNav = [
  { to: "/app/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/app/reports", label: "Reports", icon: BarChart3 },
  { to: "/app/bulk-export", label: "Bulk PDF Export", icon: Package },
  { to: "/app/parties", label: "Parties", icon: Users },
  { to: "/app/items", label: "Items / Stock", icon: Boxes },
  { to: "/app/warehouses", label: "Warehouses & Bins", icon: Warehouse },
  { to: "/app/stock-transit", label: "Stock in Transit", icon: TruckIcon },
  { to: "/app/sales", label: "All Sales", icon: ShoppingCart },
  { to: "/app/purchases", label: "All Purchases", icon: ShoppingBag },
  { to: "/app/purchase-orders", label: "Purchase Orders", icon: ClipboardCheck },
  { to: "/app/data-import", label: "Import & Tally", icon: Database },
  { to: "/app/bank", label: "Bank", icon: Landmark },
  { to: "/app/cash", label: "Cash in Hand", icon: Banknote },
  { to: "/app/loans", label: "Loans", icon: HandCoins },
  { to: "/app/manual", label: "User Manual", icon: BookOpen },
  { to: "/app/settings", label: "Settings", icon: Settings },
];

export default function AppLayout() {
  const navigate = useNavigate();
  const role = localStorage.getItem("role") || "accountant";
  const firmName = localStorage.getItem("firm_name") || "AccountifyPro";
  const nav = role === "owner" ? ownerNav : accountantNav;
  const [notifs, setNotifs] = useState([]);
  const [openBell, setOpenBell] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [currentCompanyId, setCurrentCompanyId] = useState(localStorage.getItem("company_id") || "");
  const [openCoSwitch, setOpenCoSwitch] = useState(false);

  const loadNotifs = async () => {
    try {
      const { data } = await api.get("/reports/notifications");
      setNotifs(data || []);
    } catch (err) { console.error("Notifs load failed:", err); }
  };
  const loadCompanies = async () => {
    try {
      const { data } = await api.get("/companies");
      setCompanies(data || []);
    } catch (err) { console.error("Companies load failed:", err); }
  };
  useEffect(() => {
    loadNotifs();
    loadCompanies();
    // Auto-scan cheque reminders AND overdue bills when the layout mounts
    api.post("/reports/cheques/scan-reminders").catch(() => {});
    api.post("/reports/overdue/scan").catch(() => {});
    const t = setInterval(loadNotifs, 30000);
    return () => clearInterval(t);
  }, []);

  const switchCompany = async (cid) => {
    try {
      const { data } = await api.post("/firms/switch-company", { company_id: cid });
      localStorage.setItem("token", data.token);
      localStorage.setItem("company_id", data.company_id);
      localStorage.setItem("company_name", data.company_name);
      setCurrentCompanyId(data.company_id);
      setOpenCoSwitch(false);
      window.location.reload();
    } catch (err) {
      // Accountants are locked to their company — expected 403
      if (err?.response?.status !== 403) console.error("Switch company failed:", err);
    }
  };

  const logout = () => {
    clearSession();
    navigate("/");
  };

  const switchPortal = async () => {
    try {
      const { data } = await api.post("/firms/switch-portal-token");
      localStorage.setItem("firm_token", data.firm_token);
      localStorage.setItem("firm_token_name", data.firm_name);
      localStorage.setItem("firm_id", data.firm_id);
      localStorage.removeItem("token");
      localStorage.removeItem("role");
      navigate("/firm/portal");
    } catch (e) {
      clearSession();
      navigate("/firm/login");
    }
  };

  const unread = notifs.filter((n) => !n.read).length;
  const [mobileNav, setMobileNav] = useState(false);
  const loc = useLocation();
  useEffect(() => { setMobileNav(false); }, [loc.pathname]);

  return (
    <div className="min-h-screen flex bg-[#F9FAFB]">
      {mobileNav && <div className="fixed inset-0 bg-black/40 z-40 md:hidden no-print" onClick={() => setMobileNav(false)} data-testid="mobile-nav-overlay" />}
      {/* Sidebar */}
      <aside className={`w-64 bg-white border-r border-border no-print flex-col fixed inset-y-0 left-0 z-50 md:static md:flex ${mobileNav ? "flex" : "hidden"}`} data-testid="app-sidebar">
        <div className="p-4 border-b border-border relative">
          <button className="md:hidden absolute right-3 top-3 p-1" onClick={() => setMobileNav(false)} data-testid="mobile-nav-close"><XIcon size={16} /></button>
          <div className="text-[10px] overline">ANV Associate</div>
          <div className="text-lg font-bold tracking-tight text-[#064E3B]">AccountifyPro</div>
          <div className="text-xs text-muted-foreground truncate" data-testid="sidebar-firm-name">{firmName}</div>
          <div className="mt-2 chip chip-slate uppercase tracking-widest text-[10px]" data-testid="sidebar-role">
            {role} portal
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto py-2">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 ${isActive ? "side-active" : ""}`
              }
            >
              <n.icon size={16} />
              <span>{n.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-border text-xs text-muted-foreground">
          <div className="flex items-center gap-1"><Phone size={12} /> Helpline 8446463026</div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="min-h-14 border-b border-border bg-white flex flex-wrap items-center px-3 md:px-6 py-2 gap-2 md:gap-4 no-print">
          <button className="md:hidden p-2 rounded-md border border-border" onClick={() => setMobileNav(true)} data-testid="mobile-nav-open"><Menu size={16} /></button>
          <div className="text-sm text-muted-foreground hidden sm:block">Signed in as <span className="font-semibold text-slate-800">{localStorage.getItem("firm_name")}</span></div>
          <PlanBadge />
          {companies.length > 0 && (
            <div className="relative">
              <button
                onClick={() => setOpenCoSwitch(!openCoSwitch)}
                data-testid="company-switch-btn"
                className="flex items-center gap-2 text-sm px-3 py-1.5 rounded-md border border-border hover:bg-slate-50"
                disabled={role !== "owner"}
                title={role !== "owner" ? "Accountants are locked to their assigned company" : "Switch company"}
              >
                <Building2 size={14} />
                <span className="font-semibold">
                  {(companies.find((c) => c._id_str === currentCompanyId) || {}).name || "Company"}
                </span>
                {role === "owner" && <span className="text-xs text-slate-400">▾</span>}
              </button>
              {openCoSwitch && role === "owner" && (
                <div className="absolute left-0 mt-2 w-64 bg-white border border-border rounded-md shadow-lg z-20" data-testid="company-switch-menu">
                  <div className="p-2 text-[10px] overline border-b border-border">Switch Company</div>
                  {companies.map((c) => (
                    <button
                      key={c._id_str}
                      onClick={() => switchCompany(c._id_str)}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center gap-2 ${c._id_str === currentCompanyId ? "bg-[#ECFDF5] font-semibold text-[#064E3B]" : ""}`}
                      data-testid={`company-switch-item-${c._id_str}`}
                    >
                      <Building2 size={12} />
                      <span className="flex-1 truncate">{c.name}</span>
                      {c._id_str === currentCompanyId && <span className="text-[10px]">●</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          <div className="ml-auto flex items-center gap-3">
            <div className="relative">
              <button
                onClick={() => setOpenBell(!openBell)}
                data-testid="notif-bell-btn"
                className="relative p-2 rounded-md hover:bg-slate-100"
              >
                <Bell size={18} />
                {unread > 0 && (
                  <span className="absolute -top-1 -right-1 bg-[#991B1B] text-white text-[10px] font-bold rounded-full h-4 min-w-4 px-1 flex items-center justify-center" data-testid="notif-badge">
                    {unread}
                  </span>
                )}
              </button>
              {openBell && (
                <div className="absolute right-0 mt-2 w-96 bg-white border border-border rounded-md shadow-lg z-20 max-h-96 overflow-y-auto" data-testid="notif-panel">
                  <div className="p-3 border-b border-border text-xs overline">Notifications</div>
                  {notifs.length === 0 && <div className="p-4 text-sm text-muted-foreground">No notifications yet.</div>}
                  {notifs.map((n) => (
                    <div key={n._id_str} className={`p-3 border-b border-border text-sm ${!n.read ? "bg-slate-50" : ""}`}>
                      <div className="font-semibold text-slate-800">{n.title}</div>
                      <div className="text-xs text-slate-600 mt-1">{n.body}</div>
                      <div className="text-[10px] text-muted-foreground mt-1">{new Date(n.created_at).toLocaleString()}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <button onClick={switchPortal} data-testid="switch-portal-btn" className="flex items-center gap-2 text-sm px-3 py-1.5 rounded-md border border-border hover:bg-slate-50">
              Switch to {role === "owner" ? "Accountant" : "Owner"}
            </button>
            <button onClick={logout} data-testid="logout-btn" className="flex items-center gap-2 text-sm px-3 py-1.5 rounded-md border border-border hover:bg-slate-50">
              <LogOut size={14} /> Logout
            </button>
          </div>
        </header>
        <main className="flex-1 p-3 md:p-6 overflow-y-auto overflow-x-hidden print-area">
          <Outlet />
        </main>
        <footer className="border-t border-border bg-white px-6 py-3 text-xs text-muted-foreground flex items-center justify-between no-print">
          <div>© {new Date().getFullYear()} AccountifyPro • Developed by <span className="font-semibold text-slate-700">ANV ASSOCIATE</span></div>
          <div>Helpline: 8446463026</div>
        </footer>
      </div>
    </div>
  );
}
